import MarkerClusterer from '@googlemaps/markerclustererplus';
import defineInfoWindow from './InfoWindow';

export default class LocatorResults {
  constructor() {
    const _ = this;

    // Elements
    _.$map = document.querySelector('.js-locator-map-canvas');
    _.$resultList = document.querySelector('.js-locator-result-list');
    _.$places = document.querySelectorAll('.js-locator-place');
    _.$iwTemplate = document.querySelector('#locator-infowindow-template');

    // Various vars
    _.map = null;
    _.a = [];
    _.items = [];
    _.places = [];
    _.iw = null;
    _.cluster = null;
    _.displayedPlaces = 'all';
  }

  createGetData() {
    const _= this;

    _.$form = document.querySelector('.js-locator-search');
    _.$latInput = _.$form.querySelector('.js-locator-search-lat');
    _.$lngInput = _.$form.querySelector('.js-locator-search-lng');
    _.$typeInput = _.$form.querySelector('.js-locator-search-type');
    _.$exhibitInput = _.$form.querySelector('.js-locator-search-exhibit');

    let formData = `?lat=${_.$latInput.value}`;
    formData += `&lng=${_.$lngInput.value}`;
    formData += `&type=${_.$typeInput.value}`;
    formData += `&expo=${_.$exhibitInput.value}`;
    return formData;
  }

  init() {
    const _= this;

    window.fetch(_.$resultList.dataset.locatorUrl + this.createGetData(), {
      method: 'GET',
      cache: 'default',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((response) => {
        if (!response.ok) {
          throw Error(response.statusText);
        }

        return response.json();
      })
      .then((json) => {
        _.items = json.places;
        _.generateMap();
      });
  }

  generateMap() {
    const _ = this;

    // Create the map
    _.map = new google.maps.Map(_.$map, {
      center: { lat: 46.227636, lng: 2.213749 },
      zoom: 6,
      maxZoom: 17,
      disableDefaultUI: true,
      zoomControl: true,
      clickableIcons: false
    });

    //
    if(_.items.length > 0) {
      const InfoWindow = defineInfoWindow();
      _.iw = new InfoWindow(_.map);

      // Save places data and create markers
      for (let i = 0; i < _.items.length; i++) {
        const item = _.items[i];
        const place = {
          lat: parseFloat(item['lat']),
          lng: parseFloat(item['lng']),
          zipCode: item['zipcode'],
          marker: null,
        }

        // Create marker
        const marker = new google.maps.Marker({
          position: new google.maps.LatLng(place.lat, place.lng),
          map: _.map,
          icon: _.getMarkerIcon(),

          // Custom props
          markerId: i,
        });

        marker.addListener('click', function () {
          _.toggleMarker(this);
        });

        place.marker = marker;

        _.places.push(place);
      }

      // Cluster markers
      _.cluster = new MarkerClusterer(_.map, _.places.map(place => place.marker), {
        ignoreHidden: true,
        gridSize: 80,
        styles: [{
          width: 70,
          height: 70,
          className: "c-locator__cluster",
        }],
      });

      // Close infoWindow when clicking outside of a marker
      google.maps.event.addListener(_.map, "click", function () {
        if (_.iw.marker) {
          setTimeout(function() {
            _.closeMarker(_.iw.marker);
          }, 100);
        }
      });

      _.cluster.fitMapToMarkers();
    }
  }

  getMarkerIcon() {
    return {
      path: 'M18.5 44.5c.6-.7 14.5-16.3 14.5-27C33 9 26 2 17.5 2S2 9 2 17.6c0 10.7 13.9 26.3 14.5 27 .5.6 1.5.6 2-.1zM9.4 17.2c0-4.2 3.6-7.6 8.1-7.6 4.4 0 8.1 3.4 8.1 7.6s-3.6 7.6-8.1 7.6-8.1-3.4-8.1-7.6z',
      fillColor: "#131d46",
      fillOpacity: 1,
      strokeWeight: 2,
      strokeColor: '#fff',
      anchor: new google.maps.Point(23, 34),
    };
  }

  toggleMarker(marker) {
    const _ = this;

    if (_.iw.marker && _.iw.marker === marker) {
      _.closeMarker(marker);
    } else {
      _.openMarker(marker);
    }
  }

  createInfoWindowContent(marker) {
    const _ = this;

    const place = _.items[marker.markerId];
    const details = ['title', 'address', 'phone', 'website', 'email', 'route'];
    let template = _.$iwTemplate.innerHTML;
    let toRemove = [];

    for (let j = 0; j < details.length; j++) {
      const detail = details[j];
      const regex = new RegExp(`{${detail}}`);
      if(place.hasOwnProperty(detail)) {
        if(detail === 'address') {
          const address = `${place['address']}<br>${place['zipcode']} ${place['city']}`;
          template = template.replace(regex, address);
        } else {
          template = template.replace(regex, place[detail]);
        }
      } else {
        toRemove.push(detail);
      }
    }

    const doc = new DOMParser().parseFromString(template, 'text/html');
    template = doc.body.firstChild;

    for (let j = 0; j < toRemove.length; j++) {
      template.querySelector(`[data-place-${toRemove[j]}]`).remove();
    }

    return template;
  }

  openMarker(marker) {
    const _ = this;

    _.iw.marker = marker;

    var content = _.createInfoWindowContent(marker);

    _.iw.update(marker.position || marker.center, content);
    _.iw.show();
  }

  closeMarker(marker) {
    const _ = this;

    _.iw.hide();
    _.iw.marker = null;
  }

  /**
   * Display markers found in radius of a location.
   * If no markers are found, display all.
   *
   * @param {Object} latLng - Location's coordinates to search around
   * @param {Float} latLng.lat - Location's latitude
   * @param {Float} latLng.lng - Location's longitude
   * @param {Int} kmRadius - radius to search (in kilometers)
   */
  displayInLocationRadius(latLng, kmRadius) {
    const _ = this;

    const placeIsInRadius = place => _.getDistanceBetween(latLng.lat, latLng.lng, place.lat, place.lng) <= kmRadius;

    let state = 'all';
    const foundPlaces = latLng || kmRadius ? _.places.filter(placeIsInRadius) : [];

    // Prevent processing of all places if we have to display the sames
    if (state === _.displayedPlaces) return;

    // Show or hide places
    for (let i = 0; i < _.places.length; i++) {
      const place = _.places[i];

      if (state === 'all' || (state !== 'all' && placeIsInRadius(place))) {
        // place.$element.parentNode.style.display = '';
        place.marker.setVisible(true);
      } else {
        // place.$element.parentNode.style.display = 'none';
        place.marker.setVisible(false);
      }
    }

    // Refresh map
    _.cluster.repaint();
    _.cluster.fitMapToMarkers();

    _.displayedPlaces = state;
  }

  // Return distance between two coordinates in kilometers
  // See : https://stackoverflow.com/questions/27928/calculate-distance-between-two-latitude-longitude-points-haversine-formula
  getDistanceBetween(lat1, lng1, lat2, lng2) {
    const _ = this;

    const R = 6371; // Radius of the earth in km
    const dLat = _.deg2rad(lat2 - lat1);
    const dLon = _.deg2rad(lng2 - lng1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.cos(_.deg2rad(lat1)) * Math.cos(_.deg2rad(lat2)) *  Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in km
  }

  deg2rad(deg) {
    return deg * (Math.PI/180);
  }
}
